import * as React from 'react'
import { ReactElement, useEffect, useState } from 'react'
import { css, getTheme, Icon, Link, mergeStyleSets, MessageBar, MessageBarType, Stack, StackItem, Text } from '@fluentui/react'
import { DocumentOverviewItemDto, IFolderOverviewItemDto, isDocument, isFolder } from '../../../config/documentConfig'
import { DocumentNodeType, DocumentType } from '../../../config/documentConfig'
import { formatDocumentNodeName } from '../../../helpers/documentHelper'
import DocumentListItem, { DocumentListItemProps, documentStyles } from './DocumentListItem'
import { Badge } from '@fluentui/react-components'
import LoadableText from '../../../components/LoadableText'
import { SearchResults } from './DocumentList'
import { baseListStyles } from '../BaseList'
import useIsTouchDevice from '../../../hooks/useIsTouchDevice'
import { getApplicationIconPropsByType } from '../../../helpers/iconHelper'

type Props = DocumentListItemProps<IFolderOverviewItemDto>

const theme = getTheme()
const styles = mergeStyleSets({
    childCell: { marginLeft: theme.spacing.m },
})

export const getChildCount = (children: DocumentOverviewItemDto[] | undefined, searchResults?: SearchResults): number | undefined => {
    // The number of top level documents in this folder.
    const documentCount: number | undefined = children?.filter(isDocument).filter(child => searchResults?.matchIds.has(child.id) ?? true).length

    // The number of documents contained within the folders of this folder.
    const folderChildCount: (number | undefined)[] = children?.filter(isFolder).map(folder => getChildCount(folder.children, searchResults))

    // If any child count turns out to be undefined, it means that its folder has not yet fully loaded. As such,
    // we're not able yet to determine the actual child count.
    return [documentCount, ...(folderChildCount || [])].reduce(
        (acc: number | undefined, count: number | undefined) => (acc !== undefined && count !== undefined ? acc + count : undefined),
        0,
    )
}

export default function FolderCell(props: Props): ReactElement {
    const { item: folder, items, selection } = props
    const isTouchDevice = useIsTouchDevice()
    const [childCount, setChildCount] = useState<number>()
    const [searchResults, setSearchResults] = useState<SearchResults>()
    const [isExpanded, setExpanded] = useState<boolean>(false)
    const [isSelectable, setSelectable] = useState<boolean>(false)

    useEffect(() => {
        // If the current folder is a primary result, then we want to show all children.
        setSearchResults(props.searchResults?.matchIds.has(folder.id) ? undefined : props.searchResults)
    }, [props.searchResults])

    useEffect(() => {
        setChildCount(getChildCount(folder.children, searchResults))
    }, [folder.children, searchResults])

    useEffect(() => {
        if (searchResults?.folderIds.has(folder.id)) {
            setExpanded(true)
        } else {
            setExpanded(props.isExpanded(folder.id))
        }
    }, [searchResults])

    useEffect(() => {
        // Propagate the expansion state to global when we're not in a search context, so we return to the original
        // expansions when we're done searching.
        if (!props.searchResults) {
            props.setExpanded(folder.id, isExpanded)
        }
    }, [isExpanded])

    return (
        <Stack>
            <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 's1' }}
                className={css(baseListStyles.itemCell, props.isSelected && 'is-selected')}
                onMouseEnter={() => setSelectable(true)}
                onMouseLeave={() => setSelectable(false)}
                onMouseDown={event => event.stopPropagation()}
                onClick={event => {
                    setExpanded(!isExpanded)
                    event.stopPropagation()
                }}
            >
                <StackItem>
                    <Icon {...getApplicationIconPropsByType(isExpanded ? DocumentType.Folder : DocumentType.FolderClosed, 16)} />
                </StackItem>
                <StackItem styles={{ root: baseListStyles.overflowCell }}>
                    <LoadableText isLoading={childCount === undefined}>
                        <Text variant="medium" block nowrap>
                            {formatDocumentNodeName(folder.name, DocumentNodeType.Folder)}
                        </Text>
                    </LoadableText>
                </StackItem>
                <StackItem grow styles={{ root: documentStyles.icon }}>
                    {childCount !== undefined && (
                        <Badge appearance="tint" size="small">
                            {childCount}
                        </Badge>
                    )}
                </StackItem>
                {(isSelectable || isTouchDevice || props.isSelected) && (
                    <Link
                        styles={{ root: documentStyles.icon }}
                        onClick={event => {
                            event.stopPropagation()
                            props.selection.setAllSelected(false)
                            props.selection.setIndexSelected(props.index, !props.isSelected, true)
                        }}
                    >
                        <Icon iconName="Touch" />
                    </Link>
                )}
            </Stack>
            {isExpanded && (
                <Stack styles={{ root: styles.childCell }}>
                    {folder.children?.length === 0 && <MessageBar messageBarType={MessageBarType.warning}>Deze map is leeg</MessageBar>}
                    {folder.children?.map((child: DocumentOverviewItemDto) => (
                        <DocumentListItem
                            key={child.id}
                            item={child}
                            items={items}
                            selection={selection}
                            searchResults={searchResults}
                            isExpanded={props.isExpanded}
                            setExpanded={props.setExpanded}
                            isParentSelected={props.isSelected}
                            reset={props.reset}
                        />
                    ))}
                </Stack>
            )}
        </Stack>
    )
}
